import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LockIcon from '@mui/icons-material/Lock';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import BarChartIcon from '@mui/icons-material/BarChart';
import SchoolIcon from '@mui/icons-material/School';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmojiNatureIcon from '@mui/icons-material/EmojiNature';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import FlareIcon from '@mui/icons-material/Flare';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PublicIcon from '@mui/icons-material/Public';
import InsightsIcon from '@mui/icons-material/Insights';
import { useAuth } from '../../contexts/AuthContext';
import { hasPremiumSubscription } from '../../utils/subscription';
import PremiumTrialPopup from '../PremiumTrialPopup';
import { usePremiumFeatureAccess } from '../../hooks/usePremiumFeatureAccess';
import { WEBSITE_NAVBAR_OFFSET } from './Navbar';
import { getDownloadFileName } from '../../utils/downloadFileName';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';
type ActiveTab =
  | 'nadi' | 'kp' | 'vedic' | 'bnn' | 'jaimini' | 'western' | 'advanced-tools'
  | 'tithiPravesh' | 'varshphal' | 'muhurat' | 'panchPakshi' | 'lalKitab' | 'chakra' | 'transit';

export const SIDEBAR_EXPANDED_W = 200;
export const SIDEBAR_COLLAPSED_W = 56;

const TABS: { key: ActiveTab; label: string; icon: React.ReactElement; path: (id: string) => string; premium?: boolean }[] = [
  { key: 'nadi',        label: 'Nadi Analysis',    icon: <PsychologyIcon />,    path: (id) => `/chart/${id}` },
  { key: 'kp',          label: 'KP Analysis',       icon: <AutoFixHighIcon />,   path: (id) => `/chart/${id}/kp` },
  { key: 'vedic',       label: 'Vedic Analysis',    icon: <MenuBookIcon />,      path: (id) => `/chart/${id}/divisional` },
  { key: 'bnn',         label: 'BNN Analysis',      icon: <BarChartIcon />,      path: (id) => `/chart/${id}/bnn` },
  { key: 'jaimini',     label: 'Jaimini Analysis',  icon: <SchoolIcon />,        path: (id) => `/chart/${id}/jaimini`, premium: true },
  { key: 'tithiPravesh', label: 'Tithi Pravesh',    icon: <CalendarMonthIcon />, path: (id) => `/chart/${id}/tithi-pravesh`, premium: true },
  { key: 'varshphal',   label: 'Tajika Varshphal',  icon: <CalendarMonthIcon />, path: (id) => `/chart/${id}/varshphal`, premium: true },
  { key: 'muhurat',     label: 'Muhurat',            icon: <AccessTimeIcon />,    path: (id) => `/chart/${id}/muhurat`, premium: true },
  { key: 'western',     label: 'Western Astrology', icon: <PublicIcon />,        path: (id) => `/chart/${id}/western`, premium: true },
  { key: 'advanced-tools', label: 'Advanced Tools', icon: <InsightsIcon />, path: (id) => `/chart/${id}/advanced-tools`, premium: true },
  { key: 'panchPakshi', label: 'Panch Pakshi',      icon: <EmojiNatureIcon />,   path: (id) => `/chart/${id}/panch-pakshi`, premium: true },
  { key: 'lalKitab',    label: 'Lal Kitab',         icon: <AutoStoriesIcon />,   path: (id) => `/chart/${id}/lal-kitab`, premium: true },
  { key: 'chakra',      label: 'Chakra',            icon: <FlareIcon />,         path: (id) => `/chart/${id}/chakra`, premium: true },
  { key: 'transit',     label: 'Transit',           icon: <SwapHorizIcon />,     path: (id) => `/chart/${id}/transit` },
];

const detectActive = (pathname: string): ActiveTab => {
  if (pathname.includes('/kp'))           return 'kp';
  if (pathname.includes('/divisional'))   return 'vedic';
  if (pathname.includes('/western'))      return 'western';
  if (pathname.includes('/advanced-tools')) return 'advanced-tools';
  if (pathname.includes('/bnn'))          return 'bnn';
  if (pathname.includes('/jaimini'))      return 'jaimini';
  if (pathname.includes('/tithi-pravesh')) return 'tithiPravesh';
  if (pathname.includes('/varshphal'))    return 'varshphal';
  if (pathname.includes('/muhurat'))      return 'muhurat';
  if (pathname.includes('/panch-pakshi')) return 'panchPakshi';
  if (pathname.includes('/lal-kitab'))    return 'lalKitab';
  if (pathname.includes('/chakra'))       return 'chakra';
  if (pathname.includes('/transit'))      return 'transit';
  return 'nadi';
};

interface Props {
  chartId: string;
  expanded: boolean;
  onToggle: () => void;
}

const AnalysisSidebar: React.FC<Props> = ({ chartId, expanded, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state: authState } = useAuth();
  const [pdfLoading, setPdfLoading]         = useState(false);
  const { popup, loading: trialLoading, checkFeatureAccess, closePopup } = usePremiumFeatureAccess();

  const isPremium = hasPremiumSubscription(authState.user);
  const canUsePdf = authState.isAuthenticated && !!chartId && chartId !== 'preview';
  const active      = detectActive(location.pathname);
  const w           = expanded ? SIDEBAR_EXPANDED_W : SIDEBAR_COLLAPSED_W;

  const handleTabClick = (tab: (typeof TABS)[number]) => {
    if (tab.premium && !isPremium) {
      navigate(authState.user ? '/upgrade' : '/login');
      return;
    }
    navigate(tab.path(chartId));
  };

  const downloadBlob = async (url: string, fileName: string, extraHeaders: Record<string, string> = {}) => {
    const res = await fetch(url, {
      headers: {
        ...(authState.token ? { Authorization: `Bearer ${authState.token}` } : {}),
        ...extraHeaders,
      },
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      throw new Error(payload.message || 'Download failed');
    }
    const blob    = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a       = document.createElement('a');
    a.href        = blobUrl;
    a.download    = getDownloadFileName(res, fileName);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const downloadStandardPdf = async (trialAccessToken?: string) => {
    try {
      setPdfLoading(true);
      await downloadBlob(
        `${API_BASE_URL}/charts/${chartId}/pdf`,
        'kundlistar_report.pdf',
        trialAccessToken ? { 'x-premium-trial-token': trialAccessToken } : {}
      );
      return { data: { success: true } } as any;
    } catch (e: any) {
      return {
        data: {
          success: false,
          message: e.message || 'Could not download PDF report.',
        },
        status: 500,
      } as any;
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePdfDownload = async () => {
    if (!authState.isAuthenticated) {
      window.alert('Please login to download PDF reports.');
      return;
    }

    await checkFeatureAccess({
      featureKey: 'BRANDED_PDF',
      apiCall: async (trialAccessToken?: string) => {
        const result = await downloadStandardPdf(trialAccessToken);
        if (result.data?.success === false) {
          const error = new Error(result.data.message || 'Could not download PDF report.') as Error & { response?: any };
          error.response = { data: result.data };
          throw error;
        }
        return result;
      },
      onSuccess: () => {},
    });
  };

  return (
    <>
      <Box
        sx={{
          width: w,
          minWidth: w,
          position: 'fixed',
          top: WEBSITE_NAVBAR_OFFSET,
          left: 0,
          height: {
            xs: 'calc(100vh - var(--kundlistar-navbar-height, 64px))',
            md: 'calc(100vh - var(--kundlistar-navbar-height, 160px))'
          },
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          transition: 'width 0.2s ease',
          zIndex: 9,
        }}
      >
        {/* Toggle button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: expanded ? 'flex-end' : 'center',
            p: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Tooltip title={expanded ? 'Collapse sidebar' : 'Expand sidebar'} placement="right">
            <IconButton size="small" onClick={onToggle}>
              {expanded ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Nav items */}
        <List dense sx={{ flex: 1, py: 0.5, px: 0 }}>
          {TABS.map((tab) => {
            const isActive = active === tab.key;
            const locked = Boolean(tab.premium && !isPremium);
            return (
              <Tooltip
                key={tab.key}
                title={expanded ? (locked ? 'Choose a plan to unlock' : '') : locked ? `${tab.label} - plan feature` : tab.label}
                placement="right"
              >
                <ListItemButton
                  selected={isActive}
                  onClick={() => handleTabClick(tab)}
                  sx={{
                    px: expanded ? 2 : 0,
                    py: 0.9,
                    justifyContent: expanded ? 'flex-start' : 'center',
                    borderRadius: 1,
                    mx: 0.5,
                    my: 0.2,
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'white',
                      '& .MuiListItemIcon-root': { color: 'white' },
                      '&:hover': { bgcolor: 'primary.dark' },
                    },
                    ...(locked && !isActive ? {
                      bgcolor: '#fff7e6',
                      color: 'warning.dark',
                      border: '1px solid',
                      borderColor: 'warning.light',
                      '& .MuiListItemIcon-root': { color: 'warning.dark' },
                    } : {}),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: expanded ? 34 : 0,
                      color: isActive ? 'inherit' : 'text.secondary',
                    }}
                  >
                    {React.cloneElement(tab.icon as React.ReactElement<any>, { fontSize: 'medium' })}
                  </ListItemIcon>
                  {expanded && (
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          {locked ? <LockIcon sx={{ fontSize: 14, flexShrink: 0 }} /> : null}
                          <Typography component="span" noWrap sx={{ fontSize: '0.8rem', fontWeight: isActive ? 700 : 400 }}>
                            {tab.label}
                          </Typography>
                        </Box>
                      }
                      primaryTypographyProps={{
                        component: 'div',
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>

        {/* PDF export */}
        <Divider />
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Tooltip title={canUsePdf ? 'Download PDF Report' : 'Login to download'} placement="right">
            <span style={{ width: '100%' }}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                color="success"
                startIcon={pdfLoading || trialLoading ? <CircularProgress size={13} /> : canUsePdf ? <PictureAsPdfIcon /> : <LockIcon />}
                disabled={!canUsePdf || pdfLoading || trialLoading}
                onClick={handlePdfDownload}
                sx={{
                  justifyContent: expanded ? 'flex-start' : 'center',
                  minWidth: 0,
                  px: expanded ? 1.5 : 0.75,
                  fontSize: '0.75rem',
                }}
              >
                {expanded ? 'PDF Report' : null}
              </Button>
            </span>
          </Tooltip>

        </Box>
      </Box>

      <PremiumTrialPopup
        open={popup.open}
        featureName={popup.featureName}
        used={popup.used}
        total={popup.total}
        remaining={popup.remaining}
        loading={trialLoading || pdfLoading}
        onCancel={closePopup}
        onContinue={popup.nextAction || (() => Promise.resolve())}
      />
    </>
  );
};

export default AnalysisSidebar;
